<script setup>
/**
 * O painel "Mídia, links e docs" — ocupa o LUGAR do card do contato.
 *
 * POR QUE NÃO É UM MODAL
 *
 * A primeira versão abria um Dialog centralizado. O Victor viu e recusou
 * (2026-08-07): "não quero que abra um modal no meio. É desse jeito aí, olha:
 * tenho até uma setinha depois pra voltar pro card anterior".
 *
 * O modal escurecia a conversa inteira para mostrar anexos DA conversa — e o
 * advogado precisa justamente ler a mensagem e baixar o arquivo dela. Aqui o
 * painel troca o conteúdo da coluna da direita, a conversa continua visível, e
 * a seta ← devolve o card.
 *
 * QUATRO ABAS: Mídias, Áudios, Docs, Links.
 *
 * Áudio ganhou aba própria em 21/08/2026 (pedido do Eduardo Seibert via
 * Victor: "os áudios atrapalham"). Antes entrava em Docs — a decisão original
 * era não ter aba de áudio, mas a medição derrubou: 1.490 dos 1.788 itens da
 * aba Docs eram áudio (83%), contra 292 arquivos de verdade. Ver o comentário
 * completo em `useAnexosDaConversa.js`.
 */
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import EstadoVazio from 'dashboard/components-next/EstadoVazio.vue';
import EsqueletoLista from 'dashboard/components-next/EsqueletoLista.vue';
import AudioChip from 'dashboard/components-next/message/chips/Audio.vue';
// O MESMO visualizador que a conversa usa ao clicar numa imagem da bolha:
// fundo escurecido, X para fechar, Esc e setas para navegar. Criar outro seria
// mais uma cópia ("tinha que ter paridade e interligações").
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import { useAbaPilula } from 'dashboard/routes/dashboard/legal/useAbaPilula';
import { FUSO_ESCRITORIO } from 'dashboard/helper/fusoEscritorio';
import {
  agruparPorMes,
  formatarTamanho,
  nomeDoArquivo,
  iconePorTipo,
  dominioDoLink,
} from 'dashboard/composables/useAnexosDaConversa';

const props = defineProps({
  midias: { type: Array, default: () => [] },
  audios: { type: Array, default: () => [] },
  documentos: { type: Array, default: () => [] },
  links: { type: Array, default: () => [] },
  carregando: { type: Boolean, default: false },
  falhou: { type: Boolean, default: false },
  // Na ficha do contato (05/10/2026) a aba já tem título e não há card para
  // onde voltar: o topo com a seta sai.
  semTopo: { type: Boolean, default: false },
});

const emit = defineEmits(['voltar', 'tentarDeNovo']);

const { t } = useI18n();

const abaAtiva = ref('midias');

const abas = computed(() => [
  {
    id: 'midias',
    icone: 'i-lucide-image',
    nome: t('CONVERSATION.ATTACHMENTS.MEDIA'),
    lista: props.midias,
    vazio: t('CONVERSATION.ATTACHMENTS.EMPTY_MEDIA'),
  },
  {
    id: 'audios',
    icone: 'i-lucide-mic',
    nome: t('CONVERSATION.ATTACHMENTS.AUDIO'),
    lista: props.audios,
    vazio: t('CONVERSATION.ATTACHMENTS.EMPTY_AUDIO'),
  },
  {
    id: 'docs',
    icone: 'i-lucide-file-text',
    nome: t('CONVERSATION.ATTACHMENTS.DOCS'),
    lista: props.documentos,
    vazio: t('CONVERSATION.ATTACHMENTS.EMPTY_DOCS'),
  },
  {
    id: 'links',
    icone: 'i-lucide-link',
    nome: t('CONVERSATION.ATTACHMENTS.LINKS'),
    lista: props.links,
    vazio: t('CONVERSATION.ATTACHMENTS.EMPTY_LINKS'),
  },
]);

const aba = computed(() => abas.value.find(a => a.id === abaAtiva.value));

// ABRE ONDE HÁ ALGO (05/10/2026). Abria sempre em Mídia — "Nenhuma mídia"
// numa conversa com 3 links na aba ao lado. Quando os anexos chegam e a aba
// aberta está vazia, vai para a primeira que tem conteúdo; depois que a
// pessoa escolhe uma aba, a escolha é dela.
const escolheuAba = ref(false);
const escolherAba = id => {
  escolheuAba.value = true;
  abaAtiva.value = id;
};
watch(
  abas,
  lista => {
    if (escolheuAba.value || props.carregando || aba.value?.lista.length)
      return;
    const comAlgo = lista.find(a => a.lista.length);
    if (comAlgo) abaAtiva.value = comAlgo.id;
  },
  { immediate: true }
);
const porMes = computed(() => agruparPorMes(aba.value.lista));

const caixaAbas = ref(null);
const { estiloPilula, pilulaPronta, prepararPilula } = useAbaPilula(caixaAbas);
onMounted(prepararPilula);
watch(abaAtiva, prepararPilula, { flush: 'post' });

// Qual imagem está aberta em tela cheia. `null` = fechado.
const emTelaCheia = ref(null);

// Se a MINIATURA falhar, cai para o arquivo original em vez de deixar o
// quadrado vazio. Não é hipótese: a geração de variante depende do processador
// de imagem do servidor e devolveu HTTP 500 no ambiente local (2026-08-07).
const aoFalharMiniatura = (evento, anexo) => {
  if (evento.target.dataset.tentouOriginal) return;
  evento.target.dataset.tentouOriginal = '1';
  evento.target.src = anexo.data_url;
};

// O player de áudio das mensagens lê o anexo no formato da conversa
// (`dataUrl`); o endpoint de anexos devolve `data_url`.
const comoAnexoDeMensagem = item => ({
  ...item,
  dataUrl: item.data_url,
  contentType: item.content_type,
});

// Quem procura um áudio velho reconhece pela DATA: o nome do arquivo de uma
// mensagem de voz é um hash.
const quando = item =>
  new Date(item.created_at * 1000).toLocaleString('pt-BR', {
    timeZone: FUSO_ESCRITORIO,
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

// Tom do ladrilho pelo TIPO do arquivo: PDF é o que mais se procura aqui
// (petição, procuração, RG escaneado) e ganha o rubi que o leitor de PDF usa.
const tomDoArquivo = item => {
  const ext = String(item.extension || '').toLowerCase();
  if (ext === 'pdf') return 'painel-item__ico--rubi';
  if (['doc', 'docx'].includes(ext)) return 'painel-item__ico--azul';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return 'painel-item__ico--teal';
  return '';
};
</script>

<template>
  <div class="painel">
    <!-- CABEÇALHO: a seta que devolve o card. É o que o Victor apontou na
         tela do Digisac — sem ela o painel vira um beco sem saída. -->
    <div v-if="!semTopo" class="painel-topo">
      <button
        v-tooltip.bottom="t('CONVERSATION.ATTACHMENTS.BACK')"
        type="button"
        class="advup-acao painel-voltar"
        :aria-label="t('CONVERSATION.ATTACHMENTS.BACK')"
        @click="emit('voltar')"
      >
        <i class="i-lucide-arrow-left" />
      </button>
      <h2 class="painel-titulo">
        {{ t('CONVERSATION.ATTACHMENTS.TITLE') }}
      </h2>
    </div>

    <div class="painel-abas">
      <div ref="caixaAbas" class="painel-abas__caixa" role="tablist">
        <span
          class="aba-pilula"
          :class="{ 'aba-pilula--viva': pilulaPronta }"
          :style="estiloPilula"
          aria-hidden="true"
        />
        <button
          v-for="item in abas"
          :key="item.id"
          type="button"
          role="tab"
          class="aba painel-aba"
          :class="{ 'aba--on': abaAtiva === item.id }"
          :aria-selected="abaAtiva === item.id"
          @click="escolherAba(item.id)"
        >
          <i class="aba-ico" :class="item.icone" aria-hidden="true" />
          <span class="painel-aba__nome">{{ item.nome }}</span>
          <span class="painel-aba__num">{{ item.lista.length }}</span>
        </button>
      </div>
    </div>

    <div v-if="carregando" class="painel-corpo">
      <EsqueletoLista
        :linhas="4"
        :altura="48"
        :rotulo="t('CONVERSATION.ATTACHMENTS.LOADING')"
      />
    </div>

    <EstadoVazio
      v-else-if="falhou"
      class="painel-vazio"
      tom="erro"
      icone="i-lucide-wifi-off"
      :titulo="t('CONVERSATION.ATTACHMENTS.LOAD_ERROR')"
    >
      <button
        type="button"
        class="advup-btn advup-btn--compacto"
        @click="emit('tentarDeNovo')"
      >
        <i class="i-lucide-rotate-cw" aria-hidden="true" />
        {{ t('CONVERSATION.ATTACHMENTS.RETRY') }}
      </button>
    </EstadoVazio>

    <EstadoVazio
      v-else-if="!aba.lista.length"
      :key="aba.id"
      class="painel-vazio"
      :icone="aba.icone"
      :titulo="aba.vazio"
      :dica="t('CONVERSATION.ATTACHMENTS.EMPTY_HINT')"
    />

    <div v-else :key="aba.id" class="painel-corpo">
      <section v-for="grupo in porMes" :key="grupo.rotulo" class="painel-grupo">
        <h3 class="advup-rotulo advup-rotulo--secao painel-mes">
          {{ grupo.rotulo }}
        </h3>

        <!-- 3 colunas, como no Digisac: a miniatura precisa ser grande o
             bastante para reconhecer um documento fotografado, que é o caso
             mais comum aqui. -->
        <div v-if="aba.id === 'midias'" class="painel-grade">
          <button
            v-for="(item, i) in grupo.itens"
            :key="item.message_id + item.data_url"
            v-tooltip.top="nomeDoArquivo(item)"
            type="button"
            class="painel-quadro advup-entra"
            :style="{ '--i': Math.min(i, 12) }"
            :aria-label="nomeDoArquivo(item)"
            @click="emTelaCheia = item"
          >
            <img
              v-if="item.thumb_url || item.file_type === 'image'"
              :src="item.thumb_url || item.data_url"
              alt=""
              loading="lazy"
              @error="aoFalharMiniatura($event, item)"
            />
            <span v-else class="painel-quadro__video" aria-hidden="true">
              <i class="i-lucide-play" />
            </span>
          </button>
        </div>

        <!-- Áudio toca AQUI, no mesmo player das mensagens. `preload` do
             player é por demanda: são até 67 áudios numa conversa (medido), e
             pré-carregar todos travaria o painel. -->
        <ul v-else-if="aba.id === 'audios'" class="painel-lista">
          <li
            v-for="(item, i) in grupo.itens"
            :key="item.message_id + item.data_url"
            class="painel-audio advup-entra"
            :style="{ '--i': Math.min(i, 12) }"
          >
            <AudioChip
              :attachment="comoAnexoDeMensagem(item)"
              :show-transcribed-text="false"
              class="painel-audio__player"
            />
            <span class="advup-nota painel-audio__quando">
              {{ quando(item) }}
            </span>
          </li>
        </ul>

        <ul v-else class="painel-lista">
          <li
            v-for="(item, i) in grupo.itens"
            :key="item.message_id + (item.url || item.data_url)"
            class="advup-entra"
            :style="{ '--i': Math.min(i, 12) }"
          >
            <a
              v-if="aba.id === 'links'"
              :href="item.url"
              target="_blank"
              rel="noopener noreferrer"
              class="painel-item"
            >
              <span class="painel-item__ico painel-item__ico--azul">
                <i class="i-lucide-link" aria-hidden="true" />
              </span>
              <span class="painel-item__txt">
                <span class="painel-item__nome">
                  {{ dominioDoLink(item.url) }}
                </span>
                <!-- A URL, e não o texto da mensagem. Medido no localhost
                     (2026-08-07): uma mensagem com DOIS links produzia duas
                     linhas com o mesmo subtítulo, e não dava para saber qual
                     era qual. O caminho da URL distingue. -->
                <span class="painel-item__meta">{{ item.url }}</span>
              </span>
              <i
                class="i-lucide-external-link painel-item__acao"
                aria-hidden="true"
              />
            </a>
            <a
              v-else
              :href="item.data_url"
              target="_blank"
              rel="noopener noreferrer"
              class="painel-item"
            >
              <span class="painel-item__ico" :class="tomDoArquivo(item)">
                <i :class="iconePorTipo(item)" aria-hidden="true" />
              </span>
              <span class="painel-item__txt">
                <span class="painel-item__nome">{{ nomeDoArquivo(item) }}</span>
                <span class="painel-item__meta">
                  {{
                    [formatarTamanho(item.file_size), quando(item)]
                      .filter(Boolean)
                      .join(' · ')
                  }}
                </span>
              </span>
              <i
                class="i-lucide-download painel-item__acao"
                aria-hidden="true"
              />
            </a>
          </li>
        </ul>
      </section>
    </div>

    <!-- Foto em tela cheia — o mesmo visualizador da conversa. -->
    <GalleryView
      v-if="emTelaCheia"
      :show="!!emTelaCheia"
      :attachment="emTelaCheia"
      :all-attachments="midias"
      @close="emTelaCheia = null"
    />
  </div>
</template>

<style scoped>
.painel {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: rgb(var(--surface-2));
}

.painel-topo {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: var(--esp-2);
  padding: var(--esp-3);
  border-bottom: 1px solid rgb(var(--slate-3));
}

.painel-voltar {
  background: rgb(var(--slate-3));
}

.painel-voltar:hover > i {
  animation: painel-volta var(--mov-3) var(--mov-curva-pulo);
}

@keyframes painel-volta {
  50% {
    translate: -3px 0;
  }
}

.painel-titulo {
  overflow: hidden;
  margin: 0;
  font-size: var(--txt-bloco);
  font-weight: var(--peso-forte);
  color: rgb(var(--slate-12));
  text-overflow: ellipsis;
  white-space: nowrap;
}

.painel-abas {
  flex-shrink: 0;
  container-type: inline-size;
  padding: var(--esp-2);
  border-bottom: 1px solid rgb(var(--slate-3));
}

.painel-abas__caixa {
  position: relative;
  display: flex;
  gap: var(--esp-1);
}

.painel-aba {
  flex: 1;
  justify-content: center;
  gap: var(--esp-1);
  min-width: 0;
  padding: var(--esp-2) var(--esp-1);
  font-size: var(--txt-nota);
}

.painel-aba__nome {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.painel-aba__num {
  display: inline-grid;
  place-items: center;
  flex-shrink: 0;
  min-width: 16px;
  height: 16px;
  padding: 0 var(--esp-1);
  border-radius: var(--r-redondo);
  background: rgb(var(--slate-4));
  font-size: var(--txt-selo);
  color: rgb(var(--slate-11));
  font-variant-numeric: tabular-nums;
}

.aba--on .painel-aba__num {
  background: rgb(var(--blue-9));
  color: rgb(255 255 255);
}

/* Coluna estreita (a ficha tem 320px): o ÍCONE sai, o nome fica. Com os
   dois, cada aba tinha 73px e os quatro nomes saíam cortados ("Mí…"). */
@container (max-width: 400px) {
  .painel-aba > .aba-ico {
    display: none;
  }
}

.painel-corpo {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--esp-5);
  overflow-y: auto;
  padding: var(--esp-3);
}

.painel-vazio {
  padding: var(--esp-6) var(--esp-4);
}

.painel-grupo {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
}

/* O mês fica preso no alto enquanto a lista dele rola: numa conversa de três
   meses, é ele que diz em que ponto da história a pessoa está. */
.painel-mes {
  position: sticky;
  z-index: 1;
  top: calc(-1 * var(--esp-3));
  margin: 0;
  padding: var(--esp-2) var(--esp-1);
  background: rgb(var(--surface-2));
}

.painel-grade {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--esp-2);
}

.painel-quadro {
  position: relative;
  display: grid;
  place-items: center;
  overflow: hidden;
  aspect-ratio: 1;
  padding: 0;
  border: 0;
  border-radius: var(--r-3);
  background: rgb(var(--slate-3));
  cursor: zoom-in;
}

.painel-quadro img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: scale var(--mov-3) var(--mov-curva);
}

.painel-quadro:hover img {
  scale: 1.06;
}

.painel-quadro:focus-visible {
  outline: 2px solid rgb(var(--blue-9));
  outline-offset: 2px;
}

.painel-quadro__video {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: var(--r-redondo);
  background: rgb(15 23 42 / 70%);
  color: rgb(255 255 255);
  transition: scale var(--mov-2) var(--mov-curva-pulo);
}

.painel-quadro:hover .painel-quadro__video {
  scale: 1.1;
}

.painel-lista {
  display: flex;
  flex-direction: column;
  gap: var(--esp-1);
  margin: 0;
  padding: 0;
  list-style: none;
}

.painel-audio {
  display: flex;
  flex-direction: column;
  gap: var(--esp-1);
  padding: var(--esp-2);
  border-radius: var(--r-4);
  background: rgb(var(--slate-2));
}

.painel-audio :deep(.painel-audio__player) {
  width: 100%;
}

.painel-audio__quando {
  margin: 0;
  padding-inline-start: var(--esp-1);
}

.painel-item {
  display: flex;
  align-items: center;
  gap: var(--esp-3);
  padding: var(--esp-2);
  border-radius: var(--r-3);
  text-decoration: none;
  transition: background-color var(--mov-1) var(--mov-curva);
}

.painel-item:hover {
  background: rgb(var(--slate-3));
}

.painel-item:focus-visible {
  outline: 2px solid rgb(var(--blue-9));
  outline-offset: -2px;
}

.painel-item__ico {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: var(--r-3);
  background: rgb(var(--slate-3));
  color: rgb(var(--slate-11));
}

.painel-item__ico > i {
  display: block;
  font-size: var(--ico-2);
  line-height: 1;
}

.painel-item__ico--rubi {
  background: rgb(var(--ruby-3));
  color: rgb(var(--ruby-11));
}

.painel-item__ico--azul {
  background: rgb(var(--blue-3));
  color: rgb(var(--blue-11));
}

.painel-item__ico--teal {
  background: rgb(var(--teal-3));
  color: rgb(var(--teal-11));
}

.painel-item__txt {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.painel-item__nome,
.painel-item__meta {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.painel-item__nome {
  font-size: var(--txt-corpo);
  font-weight: var(--peso-dado);
  color: rgb(var(--slate-12));
}

.painel-item__meta {
  font-size: var(--txt-nota);
  color: rgb(var(--slate-10));
}

.painel-item__acao {
  flex-shrink: 0;
  font-size: var(--ico-2);
  color: rgb(var(--slate-10));
  transition:
    color var(--mov-1) var(--mov-curva),
    translate var(--mov-2) var(--mov-curva-pulo);
}

.painel-item:hover .painel-item__acao {
  color: rgb(var(--blue-11));
  translate: 0 1px;
}

@media (prefers-reduced-motion: reduce) {
  .painel-quadro img,
  .painel-quadro__video,
  .painel-item__acao {
    transition: none;
  }

  .painel-voltar:hover > i {
    animation: none;
  }
}
</style>
